import React from "react";
import Title from "../../ui/Title";

const FamilyCaregiving = () => {
  return (
    <div id="defining-family-caregiving" className="scroll-mt-10">
      <Title title="Defining family caregiving" className="mb-4" />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          Caregivers are generally defined as people who provide care and
          support to individuals who suffer from a physical or mental
          disability, illness, or aging. Caregivers can be unpaid when they are
          family members, friends, and volunteers, while paid caregivers include
          formal healthcare workers, trained caregiving personnel, or
          professional care-providing organizations. Formal healthcare workers
          include nurses, nursing aides, or any other trained medical
          personnel.{" "}
        </p>

        <p>
          In some countries, the term ‘caregivers’ refers to formal healthcare
          workers, whereas in India and most Southeast Asian countries, it
          refers to family or informal caregivers. Family caregiving itself can
          be defined in many ways. Their meanings might overlap based on the
          country/context in which they are mentioned.
        </p>

        <h3 className="text-base font-bold mt-6 mb-3">
          <span className="font-bold text-black">Definition 1:</span>
          <a
            href="https://www.hopkinsmedicine.org/about/community-health/johns-hopkins-bayview/services/called-to-care/what-is-a-caregiver"
            className="text-blue underline ml-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            Johns Hopkins Medicine
          </a>
        </h3>

        <p className="italic">
          “The term ‘family caregiver’ describes individuals who care for
          members of their family of origin, but also refers to those who care
          for their family of choice. This could be members of their
          congregation, neighbors or close friends. Family caregivers play a
          significant role in healthcare, as they are often the main source of
          valuable information about the patient.”
        </p>

        <p>
          The terms informal caregivers and family caregivers are sometimes used
          interchangeably, as the primary caregiver may not be part of the
          family in some cases. A family or informal caregiver can be someone
          caring for a spouse or parent, an extended family member, or even a
          friend or neighbor, helping with the following tasks and more:
        </p>

        <ul className="list-disc list-outside ml-6">
          <li>Transportation to medical appointments</li>
          <li>Purchasing or organizing medications</li>
          <li>Monitoring their medical condition</li>
          <li>Communicating with healthcare professionals</li>
          <li>Advocating on their behalf with providers or agencies</li>
          <li>Getting in and out of beds or chairs</li>
          <li>Getting dressed</li>
          <li>Bathing or showering</li>
          <li>Grocery or other shopping</li>
          <li>Housework</li>
          <li>Preparing meals</li>
          <li>Managing finances</li>
        </ul>

        <h3 className="text-base font-bold !mt-10 mb-3">
          <span className="font-bold text-black">Definition 2:</span>
          <a
            href="https://www.caregiver.org/resource/definitions-0/"
            className="text-blue underline ml-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            Family Caregiving Alliance
          </a>
        </h3>

        <p className="italic">
          “A family (informal) caregiver includes any relative, partner, friend
          or neighbor who has a significant personal relationship with, and
          provides a broad range of assistance for an older person or an adult
          with a chronic or disabling condition. These individuals may be
          primary or secondary caregivers and live with, or separately from, the
          person receiving care.”
        </p>

        <h3 className="text-base font-bold !mt-10 mb-3">
          <span className="font-bold text-black">Definition 3:</span>
          <a
            href="https://www.mssocietyindia.org/wp-content/uploads/2021/05/MSSI-Caregiver-Booklet.pdf"
            className="text-blue underline ml-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            Multiple Sclerosis Society of India
          </a>{" "}
          (MSSI)
        </h3>

        <p className="italic">
          “A family caregiver is a relative who provides emotional, financial,
          nursing, social, homemaking, and other services on a daily or
          intermittent basis for an ill or disabled loved one at home. Most
          family caregivers volunteer their time, without pay, to help with the
          care needs of a loved one.”
        </p>

        <p>
          The MSSI also states that{" "}
          <span className="italic">
            “An informal caregiver is typically a family friend or neighbour,
            who provides care, typically unpaid, to someone with whom they have
            a personal relationship. This differs slightly from a family
            caregiver in that an informal caregiver is typically not directly
            related to the care recipient.”
          </span>
        </p>

        <p>
          Sources such as{" "}
          <a
            href="https://aspe.hhs.gov/sites/default/files/migrated_legacy_files//67991/faq_0.pdf"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            this document
          </a>{" "}
          from the US Department of Health and Human Services, and{" "}
          <a
            href="https://www.apa.org/pi/about/publications/caregivers/faq/family"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            this report
          </a>{" "}
          from the American Psychological Association mention what a family
          caregiver does, but not all of them have clear definitions of family
          caregiving. This{" "}
          <a
            href="https://www.ncbi.nlm.nih.gov/books/NBK604136/"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            book chapter
          </a>{" "}
          from Supporting Family Caregivers in STEMM: A Call to Action published
          in 2024 notes that,{" "}
          <span className="italic">
            “estimates of the amount of time caregivers spent on care also vary
            greatly across surveys based on differing definitions of caregiving
            and what is counted as caregiving labor."
          </span>
        </p>

        <p>
          In a September 2019{" "}
          <a
            href="https://unece.org/DAM/pau/age/Policy_briefs/ECE_WG1_31.pdf"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            policy brief
          </a>{" "}
          titled{" "}
          <span className="italic">
            The challenging roles of informal carers, the United Nations
            Economic Commission for Europe stated that “the term informal carer
            is often used interchangeably with the term ‘family carer’. Some
            notions of ‘informal carers’ therefore, tend to exclude paid but
            non-professional carers providing care in private households. Others
            focus only on those providing informal care to older people with
            care needs.”
          </span>{" "}
          The brief further defines informal caregiver as{" "}
          <span className="italic">
            “all non-professional care provided — by choice or by default — by
            family members (next of kin), friends, neighbours, or other persons
            caring for people with long-term care needs at all ages, usually in
            private households.”
          </span>
        </p>

        <h3 className="text-base font-bold text-black mt-6 mb-3">
          Other types of caregivers
        </h3>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Home healthcare caregivers: Formal caregivers like nurses or nursing
            aides that visit patients homes to give care otherwise delivered at
            hospitals
          </li>
          <li>
            Non-medical in-home caregivers: Individuals who assist in daily
            living activities that do not involve any specialized skills
          </li>
          <li>
            Independent/private duty caregivers: Caregivers who are trained for
            special caregiving purposes and have some level of medical training
            within retirement living spaces, hospitals, and other organizations
          </li>
          <li>
            Respite caregivers: Those who come for a few hours or some time
            during the day or week to relieve family caregivers for a period of
            time
          </li>
          <li>
            Adult daycare services: Facilities where caregivers can bring their
            patients to spend the day or a few hours at a center that has
            special aids and facilities
          </li>
          <li>
            Hospice caregivers: Care providers, mostly within palliative care
            centers, who provide pain management and assistance with daily
            activities
          </li>
          <li>
            Virtual/long distance caregivers: Family caregivers who assist
            online in daily activities related to caregiving, or provide other
            household management support virtually
          </li>
        </ul>

        <p>
          As mentioned above, there are paid and unpaid caregivers. From the
          types of caregivers mentioned, the following table segregates them
          into paid and unpaid.
        </p>

        <div className="overflow-x-auto my-6">
          <table className="min-w-full border border-[#FBAB20] bg-[#FEFBF6]">
            <thead>
              <tr className="text-black">
                <th className="border border-[#FBAB20] px-4 py-3 text-left font-bold w-1/2 align-top">
                  Paid caregivers
                </th>
                <th className="border border-[#FBAB20] px-4 py-3 text-left font-bold w-1/2 align-top">
                  Unpaid caregivers
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Home health aides
                </td>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Family caregivers
                </td>
              </tr>
              <tr>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Personal care assistants: Non-medical in-home care
                </td>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Informal caregivers: Not necessarily family members;
                  individuals helping with chores, companionship, and more
                </td>
              </tr>
              <tr>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Respite caregivers, adult day cares: Short-term or long- term
                  care
                </td>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Virtual/long distance caregivers: Primarily family, who
                  provide remote support, such as managing finances, health
                  records, appointments, and other online or administrative
                  tasks
                </td>
              </tr>
              <tr>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Hospice services
                </td>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Volunteers
                </td>
              </tr>
              <tr>
                <td className="border border-[#FBAB20] px-4 py-3 align-top">
                  Formal healthcare workers: Nurses,nursing assistants etc.
                </td>
                <td className="border border-[#FBAB20] px-4 py-3 align-top"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <p
          id="table-note-terminology"
          className="italic text-xs text-center scroll-mt-24"
        >
          Note: This terminology may be different depending on country context
        </p>
      </div>
    </div>
  );
};

export default FamilyCaregiving;
